<div class="container">

  <header class="page-heading">
    <h1>NPS Calculator</h1>
    <p>Calculate your National Pension System (NPS) corpus at retirement, expected lumpsum withdrawal and monthly pension.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">NPS / Retirement Corpus Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('currentAge')">
        <label class="field-label" for="currentAge">Current Age</label>
        <div class="input-shell suffix">
          <input id="currentAge" type="number"
            [(ngModel)]="currentAge"
            [min]="C.age.min" [max]="C.age.max" step="1"
            (ngModelChange)="onFieldChange('currentAge')"
            (blur)="onFieldBlur('currentAge')"
            placeholder="e.g. 30"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('currentAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentAge') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('retirementAge')">
        <label class="field-label" for="retirementAge">Retirement Age</label>
        <div class="input-shell suffix">
          <input id="retirementAge" type="number"
            [(ngModel)]="retirementAge"
            [min]="C.age.min" max="60" step="1"
            (ngModelChange)="onFieldChange('retirementAge')"
            (blur)="onFieldBlur('retirementAge')"
            placeholder="e.g. 60"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('retirementAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('retirementAge') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyContribution')">
        <label class="field-label" for="monthlyContribution">Monthly Contribution</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyContribution" type="number"
            [(ngModel)]="monthlyContribution"
            [min]="C.monthlyContribution.min" [max]="C.monthlyContribution.max" [step]="C.monthlyContribution.step"
            (ngModelChange)="onFieldChange('monthlyContribution')"
            (blur)="onFieldBlur('monthlyContribution')"
            placeholder="e.g. 10,000"
            autocomplete="off"/>
        </div>
        @if (hasError('monthlyContribution')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyContribution') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Expected Annual Return</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 10"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('annuityPercent')">
        <label class="field-label" for="annuityPercent">Annuity Allocation</label>
        <div class="input-shell suffix">
          <input id="annuityPercent" type="number"
            [(ngModel)]="annuityPercent"
            [min]="C.annuityPercent.min" [max]="C.annuityPercent.max" [step]="C.annuityPercent.step"
            (ngModelChange)="onFieldChange('annuityPercent')"
            (blur)="onFieldBlur('annuityPercent')"
            placeholder="e.g. 40"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">At least 40% of the corpus must go into an annuity by rule.</div>
        @if (hasError('annuityPercent')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annuityPercent') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annuityRate')">
        <label class="field-label" for="annuityRate">Expected Annuity Rate</label>
        <div class="input-shell suffix">
          <input id="annuityRate" type="number"
            [(ngModel)]="annuityRate"
            [min]="C.annuityRate.min" [max]="C.annuityRate.max" [step]="C.annuityRate.step"
            (ngModelChange)="onFieldChange('annuityRate')"
            (blur)="onFieldBlur('annuityRate')"
            placeholder="e.g. 6"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annuityRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annuityRate') }}
          </div>
        }
      </div>
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">NPS Corpus at Retirement</div>
        <div class="sc-value">{{ fmt.currency(result.corpus) }}</div>
        <div class="sc-sub">at age {{ retirementAge }} ({{ tenureYears }} years)</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Lumpsum Withdrawable</div>
        <div class="sc-value">{{ fmt.currency(result.lumpsumAmount) }}</div>
        <div class="sc-sub">{{ 100 - annuityPercent }}% of corpus</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Expected Monthly Pension</div>
        <div class="sc-value">{{ fmt.currency(result.monthlyPension) }}</div>
        <div class="sc-sub">from {{ fmt.currency(result.annuityAmount) }} annuity</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Invested vs Gain</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Corpus Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-wise Growth</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Age</th>
              <th>Total Invested</th>
              <th class="t-balance">Corpus Value</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">{{ row.age }}</td>
                <td>{{ fmt.currency(row.invested) }}</td>
                <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is NPS Retirement Corpus Calculated?</h2>
    <p>
      The National Pension System (NPS) is a market-linked retirement scheme. Your monthly
      contributions compound at an expected annual return until retirement (currently capped at
      age 60 for withdrawal purposes). At retirement, at least 40% of the corpus must be used to
      purchase an annuity that pays you a monthly pension; the rest can be withdrawn as a lumpsum.
    </p>
    <p>
      This calculator compounds your monthly contributions at your expected return, then splits
      the final corpus into a lumpsum and an annuity based on your chosen allocation, and estimates
      the resulting monthly pension using your expected annuity rate.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is the NPS lumpsum withdrawal taxable?</h3>
      <p>
        Up to 60% of the corpus can be withdrawn as a lumpsum, and this portion is entirely tax-free
        under current rules. The remaining annuity portion is taxed as regular income when received
        as pension.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can I choose to allocate more than 40% to the annuity?</h3>
      <p>
        Yes. 40% is only the minimum mandatory allocation &mdash; you can choose to annuitize a
        larger share of your corpus for a higher guaranteed monthly pension.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/nps-vs-epf">NPS vs EPF: which retirement account wins?</a>
    </p>
  </div>
</section>
